/**
 * Marketing-side pricing catalog. Mirrors the server source of truth in
 * `artifacts/api-server/src/routes/billing.ts` (PLAN_CATALOG +
 * CREDIT_PACKS). Kept in sync manually for the public Tarifs page —
 * the signed-in Réglages → Forfait & crédits screen still pulls
 * `/api/billing/me` so any change on the server is reflected there
 * automatically.
 */

/**
 * TVA française standard appliquée aux services numériques (20 %).
 * Les prix de référence affichés en boutique et facturés correspondent
 * au montant TTC (cf. CGV § 2). Le HT est calculé pour transparence.
 */
export const VAT_RATE = 0.2;

export function priceHtFromTtc(ttc: number): number {
  return Math.round((ttc / (1 + VAT_RATE)) * 100) / 100;
}

export function formatEur(value: number): string {
  return value.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export interface PublicPlan {
  id: "free" | "pro" | "unlimited";
  label: string;
  monthlyPriceEur: number;
  monthlyCredits: number | null;
  tagline: string;
  features: string[];
  cta: string;
  highlight?: boolean;
}

// Labels strictement alignés sur PLAN_LABELS côté serveur
// (artifacts/api-server/src/lib/aiCredits.ts).
export const PUBLIC_PLANS: PublicPlan[] = [
  {
    id: "free",
    label: "Gratuit",
    monthlyPriceEur: 0,
    monthlyCredits: 50,
    tagline: "Pour découvrir OneNext sans engagement.",
    features: [
      "50 crédits IA / mois",
      "1 boîte mail connectée",
      "Tâches, projets, capture éclair illimités",
      "Page Aujourd'hui et tri des mails",
      "Support communautaire",
    ],
    cta: "Créer un compte gratuit",
  },
  {
    id: "pro",
    label: "Pro",
    monthlyPriceEur: 19,
    monthlyCredits: 1000,
    tagline: "Pour les indépendants et les équipes produit.",
    features: [
      "1 000 crédits IA / mois",
      "Boîtes mail illimitées",
      "Assistant conversationnel complet",
      "Suggestions d'actions sur tous les mails",
      "Historique de facturation et reçus",
      "Support par e-mail sous 48 h",
    ],
    cta: "Choisir Pro",
    highlight: true,
  },
  {
    id: "unlimited",
    label: "Illimité",
    monthlyPriceEur: 49,
    monthlyCredits: null,
    tagline: "Pour les utilisateurs intensifs.",
    features: [
      "Crédits IA illimités (usage raisonnable)",
      "Boîtes mail illimitées",
      "Toutes les fonctionnalités Pro",
      "Priorité sur les nouvelles fonctionnalités",
      "Support prioritaire sous 24 h",
    ],
    cta: "Choisir Illimité",
  },
];

export interface PublicCreditPack {
  id: string;
  label: string;
  credits: number;
  priceEur: number;
}

export const PUBLIC_CREDIT_PACKS: PublicCreditPack[] = [
  { id: "pack100", label: "100 crédits", credits: 100, priceEur: 5 },
  { id: "pack500", label: "500 crédits", credits: 500, priceEur: 19 },
  { id: "pack1000", label: "1 000 crédits", credits: 1000, priceEur: 35 },
];

export interface FaqItem {
  q: string;
  a: string;
}

export const PUBLIC_FAQ: FaqItem[] = [
  {
    q: "Mes e-mails et identifiants sont-ils en sécurité ?",
    a: "Oui. Les mots de passe IMAP/SMTP de vos boîtes mail connectées sont chiffrés au repos en AES-256-GCM. Toutes les communications utilisent TLS. L'authentification est gérée par Clerk avec MFA optionnel.",
  },
  {
    q: "Êtes-vous conformes au RGPD ?",
    a: "Oui. OneNext est conçu en conformité avec le RGPD. L'hébergement applicatif et la base de données sont en Europe. Vous pouvez à tout moment exporter (article 20) ou supprimer (article 17) vos données depuis Réglages → Mes données. Un DPA téléchargeable est disponible dans les documents légaux.",
  },
  {
    q: "Quels modèles d'IA sont utilisés ? Mes données entraînent-elles les modèles ?",
    a: "OneNext utilise l'API d'OpenAI pour le tri des mails, les suggestions de tâches et l'assistant conversationnel. Conformément aux conditions d'OpenAI pour l'API, vos données ne sont pas utilisées pour entraîner les modèles. Elles sont conservées au maximum 30 jours par OpenAI à des fins de modération, puis supprimées.",
  },
  {
    q: "Puis-je résilier mon abonnement à tout moment ?",
    a: "Oui. Aucun engagement de durée. La résiliation s'effectue depuis Réglages → Forfait & crédits. Elle prend effet à la fin de la période en cours et vous gardez l'accès jusque-là.",
  },
  {
    q: "Y a-t-il un remboursement si je me désabonne ?",
    a: "Lors de la souscription, vous bénéficiez immédiatement du service et renoncez expressément à votre droit de rétractation (article L221-28 du Code de la consommation). Les abonnements en cours ne sont pas remboursés au prorata. Voir notre politique de remboursement détaillée pour les exceptions et les achats de packs de crédits.",
  },
  {
    q: "Comment supprimer mon compte et toutes mes données ?",
    a: "Depuis Réglages → Mes données (RGPD), un seul bouton supprime définitivement votre compte et l'ensemble des données associées : tâches, projets, e-mails synchronisés, identifiants chiffrés. La suppression est immédiate et irréversible.",
  },
  {
    q: "Où sont hébergées mes données ?",
    a: "L'application et la base de données PostgreSQL sont hébergées en Europe (Google Cloud, région européenne). Les sous-traitants Clerk (authentification) et OpenAI (IA) opèrent depuis les États-Unis ; les transferts sont encadrés par les clauses contractuelles types adoptées par la Commission européenne.",
  },
  {
    q: "Que se passe-t-il si j'épuise mes crédits IA ?",
    a: "L'usage non-IA de OneNext (tâches, projets, capture, lecture des mails) reste fonctionnel. Pour relancer les fonctionnalités d'IA, vous pouvez acheter un pack de crédits ponctuel ou passer à un forfait supérieur. Aucun débit surprise : vous restez maître de vos dépenses.",
  },
  {
    q: "Comment vous contacter ?",
    a: "Par le formulaire de contact ou par e-mail à l'adresse indiquée dans les Mentions légales. Les utilisateurs Pro reçoivent une réponse sous 48 h ouvrées, les utilisateurs Unlimited sous 24 h ouvrées.",
  },
];
